/**
 * "New workspace" modal dialog. Mirrors CreateProjectDialog UX.
 */

import { useState } from 'react'

interface CreateWorkspaceDialogProps {
  onClose: () => void
  onSubmit: (input: { name: string; slug: string }) => Promise<void> | void
  busy: boolean
}

export function CreateWorkspaceDialog({ onClose, onSubmit, busy }: CreateWorkspaceDialogProps) {
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)

  // Auto-derive slug: lowercase, dashes, strip invalid chars.
  const derivedSlug = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)
  const effectiveSlug = slugTouched ? slug : derivedSlug
  const canSubmit = name.trim().length > 0 && effectiveSlug.length > 0

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-sm p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-1 text-base font-semibold">New workspace</h2>
        <p className="mb-4 text-xs text-surface-500">
          Workspaces hold projects and members. Pick a short, memorable name — the slug
          is used in URLs.
        </p>
        <form
          onSubmit={async (e) => {
            e.preventDefault()
            if (!canSubmit) return
            await onSubmit({ name: name.trim(), slug: effectiveSlug })
          }}
          className="space-y-3"
        >
          <label className="block">
            <span className="text-xs font-medium text-surface-600 dark:text-surface-300">
              Name
            </span>
            <input
              autoFocus
              className="input mt-1"
              placeholder="e.g. Acme Studio"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
              maxLength={60}
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-surface-600 dark:text-surface-300">
              Slug
            </span>
            <input
              className="input mt-1 font-mono"
              placeholder="acme-studio"
              value={effectiveSlug}
              onChange={(e) => {
                setSlug(e.target.value.toLowerCase())
                setSlugTouched(true)
              }}
              disabled={busy}
              maxLength={32}
            />
            <span className="mt-1 block text-[10px] text-surface-500">
              Auto-derived from name; edit only if you need to.
            </span>
          </label>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              className="btn-ghost"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={!canSubmit || busy}
            >
              {busy ? 'Creating…' : 'Create workspace'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}